import React, {useRef, useEffect, useState} from 'react'
import moment from 'moment'

import OrderActions from '../../actions/OrderActions.js'
import useVehicleStore from '../../stores/VehicleStore.js'

import {Badge, Card, Icon, S1, ToolTip, Row, Stack, Popover, ListItem, IconButton, Colors, P, Popup, Spinner, Button} from '../UI/index.js'
import {formSpacing, ColorPicker, FormRow, Select, Check} from '../UI/forms/index.js'
import time from '../../../server/utils/time.js'
import {formatDuration} from '../../../utils/utils.js'
import {User} from '../../../server/functions/users/users.types.js'
import {FrontEndRoute} from '../../../server/functions/route/route.types.js'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import {getColliTypeName} from '../../../utils/getColliTypeName.js'
import stopBundles from '../../../utils/stopBundles.js'
import Languages from '@utils/Languages.js'
import t from '../../helpers/Languages.js'
import RouteStatusBadge from './RouteStatusBadge.js'
import {canRemoveRoute, doesRouteOverlapOn, getCompletedRouteDuration, getRemoveRouteConfirm, getRouteLoadIssues, getRouteOverlaps, getRouteStatus, getRouteReadyWarnings, getRouteTimeSlots, uniqueColli} from './routeSummary.js'
import type {PopupRef} from '../UI/overlays/Popup.js'
import {getRouteBulkActionDisabledReason, RouteBulkAction} from '../../utils/routeBulkActions.js'
import {getETADeviation, formatETADeviationTime} from '@utils/routeETA.js'

type RouteOverlapData = {
    overlappingVehicleIds: Record<string, boolean>
    overlappingMessengerIds: Record<string, boolean>
    routeOverlaps: boolean
    messengerOverlaps: boolean
}

type VehicleSelectEvent = {
    target: {
        value: string
        subvalue?: string
    }
}

interface RouteListItemProps {
    users: User[]
    route: FrontEndRoute
    selectedRouteId: string
    focusedRouteId: string
    onEditRoute: (route: FrontEndRoute) => void
    reseller: Reseller
    routeOverlapData?: RouteOverlapData
    routes?: Record<string, FrontEndRoute>
    view: string
    bulkAction?: RouteBulkAction | ''
    bulkSelected?: boolean
    bulkRunning?: boolean
    onToggleBulkSelect?: (routeId: string, event?: {shiftKey?: boolean}) => void
}

export default React.memo(function RouteListItem({users, route, selectedRouteId, focusedRouteId, onEditRoute, reseller, routeOverlapData, routes, view, bulkAction, bulkSelected, bulkRunning, onToggleBulkSelect}: RouteListItemProps) {
    const popupRef = useRef<PopupRef>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const [titleIsEllipsis, setTitleIsEllipsis] = useState(false)
    const selected = selectedRouteId === route._id
    const vehicles = useVehicleStore((state) => state.vehicles)
    // Hidden for now, shown with localStorage.showSuggestedStartTime = 'true'
    const showSuggestedStartTime = !!route.suggestedStartTime && !route.started && localStorage.getItem('showSuggestedStartTime') === 'true'

    // Not on the route: that is the OrderStore's
    const {afterTimeSlot, inTimeSlots} = getRouteTimeSlots(route)

    const {completedDuration, completedDelay} = getCompletedRouteDuration(route)

    const routeHasNotStartedAddresses = route.started && route.addresses?.some((address) => !address.underway)

    const amountOfColli = uniqueColli(route.colli).length

    const {tooMuchColli, tooHeavy, tooFar} = getRouteLoadIssues(route, reseller)

    const etaDeviation = getETADeviation(route)

    const routeList = routes ? Object.values(routes) : []

    // The planning passes the overlaps it computed for all routes at once, else from the routes
    const overlapsFromRoutes = routeOverlapData ? undefined : getRouteOverlaps(route, routeList, users)

    const overlappingVehicleIds = routeOverlapData?.overlappingVehicleIds || {}
    const overlappingMessengerIds = routeOverlapData?.overlappingMessengerIds || {}
    const routeOverlaps = routeOverlapData?.routeOverlaps ?? overlapsFromRoutes?.routeOverlaps
    const messengerOverlaps = routeOverlapData?.messengerOverlaps ?? overlapsFromRoutes?.messengerOverlaps

    const bulkDisabledReason = bulkAction ? getRouteBulkActionDisabledReason(bulkAction, route) : ''

    const startTime = (route.plannedStartTime || route.startTime) ? moment(route.plannedStartTime ? `${route.date} ${route.plannedStartTime}` : route.startTime).format('HH:mm') : ''

    // The name gets the room the start time, label and menu leave, which differs per route and changes with the
    // panel width, so measure again on every resize
    useEffect(() => {
        const el = titleRef.current
        if (!el) return

        const measure = () => setTitleIsEllipsis(el.offsetWidth < el.scrollWidth)
        measure()

        if (typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(measure)
        observer.observe(el)

        return () => observer.disconnect()
    }, [route.name])

    const card = (
        <Card key={route._id} data-route-id={route._id} shadow={true} selected={selected} style={{padding: 0, marginBottom: 6, marginLeft: bulkAction ? 6 : 0, minWidth: 0, scrollMarginTop: 20}}>
            <Row style={{flexShrink: 0, height: '100%', alignItems: 'stretch'}}>
                <Stack style={{flex: 1, padding: 6}}>
                    <Row style={{flexShrink: 0, alignItems: 'center'}}>
                        {route.completed ?
                            <Icon
                                style={{marginRight: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, fontSize: 20, color: route.color}}
                                icon='mdi mdi-circle'
                            /> :
                            <ColorPicker
                                value={route.color}
                                colors={Colors.routeColors}
                                onChange={(event) => OrderActions.updateRoute(route._id, {color: event.target.value})}
                            />
                        }

                        {/* The name runs on up to the start time, or up to the label when the route has none */}
                        <Row style={{flex: 1, alignItems: 'center', overflowX: 'hidden'}}>
                            {view !== 'expanded' && !selected && (tooMuchColli || tooHeavy || tooFar || routeOverlaps || messengerOverlaps || ((route.plannedStartTime || route.startTime) && (!inTimeSlots && afterTimeSlot))) &&
                                <Icon icon='mdi mdi-alert' style={{marginRight: 6, color: Colors.errorBright}} />
                            }

                            <ToolTip disabled={!titleIsEllipsis} placement='right' style={{flex: 1, minWidth: 0, overflowX: 'hidden'}} tooltipStyle={{width: 'fit-content', whiteSpace: 'nowrap', padding: '0 8px', maxWidth: 350}} text={route.name}>
                                <S1 ref={titleRef} ellipsis style={{cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>{route.name}</S1>
                            </ToolTip>
                        </Row>

                        <Row style={{width: 'auto', flexShrink: 0, alignItems: 'center', cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>
                            {/* The card is narrower with the bulk checkbox in front of it, so leave out the start time to keep room for the route name */}
                            {!bulkAction && !showSuggestedStartTime && startTime &&
                                <P style={{marginLeft: 12, whiteSpace: 'nowrap'}}>{startTime}</P>
                            }

                            {!bulkAction && showSuggestedStartTime &&
                                // The optimiser advises a start time for a route without a planned start, shown in its place;
                                // the time and icon open a popover to accept it (sets it as the planned start) or reject it
                                <Row style={{marginLeft: 12, width: 62, alignItems: 'center'}} onClick={(event) => event.stopPropagation()}>
                                    <Popover
                                        placement='left'
                                        popoverStyle={{width: 260, whiteSpace: 'normal', padding: 12}}
                                        content={
                                            <Stack style={{gap: 6}}>
                                                <S1>{t('suggestedStartTimeTitle', {time: route.suggestedStartTime})}</S1>

                                                <P>{t('suggestedStartTimeText')}</P>

                                                <Row style={{justifyContent: 'flex-end', marginTop: 6}}>
                                                    <Button
                                                        variant='text'
                                                        onClick={() => OrderActions.updateRoute(route._id, {suggestedStartTime: ''})}
                                                    >
                                                        {t('ignore')}
                                                    </Button>

                                                    <Button onClick={() => OrderActions.updateRoute(route._id, {plannedStartTime: route.suggestedStartTime})}>
                                                        {t('adopt')}
                                                    </Button>
                                                </Row>
                                            </Stack>
                                        }
                                    >
                                        <Row style={{alignItems: 'center', cursor: 'pointer'}}>
                                            <P style={{color: Colors.infoBright}}>{route.suggestedStartTime}</P>
                                            <Icon style={{marginLeft: 4, marginRight: 0, fontSize: 16, color: Colors.infoBright}} icon='mdi mdi-lightbulb-on-outline' />
                                        </Row>
                                    </Popover>
                                </Row>
                            }

                            {/* As wide as the widest label (an invisible Onderweg under it), so the start time stays put between statuses */}
                            <div style={{display: 'grid', justifyItems: 'end', marginLeft: 12}}>
                                <div style={{gridArea: '1 / 1', visibility: 'hidden'}} aria-hidden>
                                    <RouteStatusBadge status='started' />
                                </div>

                                <div style={{gridArea: '1 / 1'}}>
                                    {/* A started route shows how far it runs ahead or behind instead of Onderweg */}
                                    {route.started && !route.completed ?
                                        <Badge color={etaDeviation > 0 ? Colors.errorDark : Colors.successDark}>{formatETADeviationTime(etaDeviation)}</Badge> :
                                        <RouteStatusBadge status={getRouteStatus(route)} />
                                    }
                                </div>
                            </div>
                        </Row>

                        {!route.completed &&
                            <Popover
                                placement='left'
                                content={
                                    <div style={{width: 'fit-content'}}>
                                        <ListItem
                                            size='sm'
                                            icon={focusedRouteId === route?._id ? 'mdi mdi-image-filter-center-focus-weak' : 'mdi mdi-image-filter-center-focus-strong'}
                                            onClick={() => OrderActions.setRouteFocus(route?._id)}
                                        >

                                            {route._id === focusedRouteId ? 'Reset focus' : 'Focus route'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon={route.hidden ?
                                                'mdi mdi-map-marker' :
                                                'mdi mdi-map-marker-off'
                                            }
                                            onClick={() => OrderActions.setRouteHidden(route._id)}
                                        >

                                            {route.hidden ? 'Toon route' : 'Verberg route'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-tune'
                                            onClick={() => onEditRoute(route)}
                                        >
                                            Route instellingen
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-map-marker-path'
                                            disabled={route.ready}
                                            onClick={() => OrderActions.planRoute(route._id, true)}
                                        >
                                            Optimaliseer route
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-swap-vertical'
                                            disabled={route.ready || route.started || !route.reversible}
                                            onClick={() => OrderActions.planRoute(route._id, false, true)}
                                        >
                                            Route omdraaien
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon={route.ready ? 'mdi mdi-pencil' : 'mdi mdi-check'}
                                            onClick={async () => {
                                                if (route.ready) {
                                                    if (!await popupRef.current.open('Route wijzigen', 'Weet je zeker dat je deze route wilt wijzigen? ')) return
                                                } else {
                                                    const warnings = getRouteReadyWarnings({
                                                        tooMuchColli,
                                                        tooHeavy,
                                                        tooFar,
                                                        outsideTimeSlots: !!((route.plannedStartTime || route.startTime) && !inTimeSlots),
                                                        routeOverlaps,
                                                        messengerOverlaps
                                                    })

                                                    if (warnings.length) {
                                                        popupRef.current.setWarnings(warnings)
                                                        if (!await popupRef.current.open('Route gereed maken', 'Weet je zeker dat je door wil gaan?')) return
                                                    }
                                                }

                                                OrderActions.setRouteReady(route._id, !route.ready, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >
                                            {route.ready ? 'Route wijzigen' : 'Route gereed'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-calendar'
                                            onClick={async () => {
                                                popupRef.current.setInput({type: 'date', value: time.tomorrow(), label: 'Datum'})
                                                const date = await popupRef.current.open('Route verplaatsen', 'Weet je zeker dit zeker?')
                                                if (date === undefined) return

                                                OrderActions.rescheduleRoute(route._id, date, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >

                                            Route verplaatsen
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-flag-checkered'
                                            disabled={!route.completedAddresses?.length || route.completed}
                                            onClick={async () => {
                                                if (route.addresses?.length) {
                                                    if (!await popupRef.current.open('Route afronden', 'Niet afgeronde stops worden uit de route verwijderd. Weet je zeker dat je deze route wilt afronden?')) return
                                                } else {
                                                    if (!await popupRef.current.open('Route afronden', 'Weet je zeker dat je deze route wilt afronden?')) return
                                                }

                                                OrderActions.stopRoute(route._id, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Route afronden
                                        </ListItem>

                                        <ListItem
                                            style={{color: canRemoveRoute(route) ? Colors.errorBright : undefined}}
                                            size='sm'
                                            icon='mdi mdi-delete'
                                            disabled={!canRemoveRoute(route)}
                                            onClick={async () => {
                                                const confirm = getRemoveRouteConfirm(route)

                                                if (confirm && !await popupRef.current.open(confirm.title, confirm.text)) return

                                                OrderActions.removeRoute(route._id, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Route verwijderen
                                        </ListItem>
                                    </div>
                                }
                            >
                                <IconButton icon='mdi mdi-dots-vertical' />
                            </Popover>
                        }
                    </Row>

                    {(view === 'expanded' || selected) &&
                        <>
                            <FormRow style={{marginBottom: formSpacing.rowGap}}>
                                <Select
                                    label='Koerier'
                                    allowEmpty
                                    options={users.map((user) => {
                                        const thisUserWouldOverlap = routeOverlapData ?
                                            !!overlappingMessengerIds[user._id] :
                                            doesRouteOverlapOn(route, routeList, 'messengerId', user._id)

                                        const thisUserOverlaps = messengerOverlaps && route.messengerId === user._id

                                        let icon

                                        if (thisUserWouldOverlap && !thisUserOverlaps) {
                                            icon = 'mdi mdi-cancel'
                                        }

                                        return {value: user._id, title: user.name, ...(icon && {icon})}
                                    })}
                                    value={route.messengerId}
                                    readOnly={route.completed}
                                    onChange={(event) => OrderActions.updateRoute(route._id, {messengerId: event.target.value})}
                                    isInvalid={messengerOverlaps}
                                    isInvalidIcon={messengerOverlaps ? 'mdi mdi-alert' : undefined}
                                />

                                <Select
                                    label='Voertuig'
                                    options={[
                                        // This sorting is overruled in the select component by the groupSubvalues prop
                                        ...[...vehicles].sort((a, b) => {
                                            const aInFutureRange = a.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))
                                            const bInFutureRange = b.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))

                                            const aArchived = a.isArchived ? 1 : 0
                                            const bArchived = b.isArchived ? 1 : 0

                                            if (aArchived !== bArchived) {
                                                return aArchived - bArchived
                                            } else if (aInFutureRange && bInFutureRange) {
                                                return (aInFutureRange.end > bInFutureRange.end) ? 1 : -1
                                            } else if (aInFutureRange) {
                                                return 1
                                            } else if (bInFutureRange) {
                                                return -1
                                            }

                                            return 0
                                        }).map((vehicle) => {
                                            const activeVehicleType = reseller.settings.vehicleTypes.find((type) => type._id === vehicle.typeId)

                                            const thisVehicleWouldOverlap = routeOverlapData ?
                                                !!overlappingVehicleIds[vehicle._id] :
                                                doesRouteOverlapOn(route, routeList, 'vehicleId', vehicle._id)

                                            const thisVehicleOverlaps = routeOverlaps && route.vehicleId === vehicle._id

                                            const inFutureRange = vehicle.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))

                                            let icon = activeVehicleType?.icon
                                            if (thisVehicleWouldOverlap && !thisVehicleOverlaps) {
                                                icon = 'mdi mdi-cancel'
                                            }

                                            return {
                                                value: activeVehicleType?._id,
                                                subvalue: vehicle._id,
                                                title: vehicle.name,
                                                icon,
                                                hidden: activeVehicleType?.isArchived || vehicle.isArchived,
                                                disabled: inFutureRange
                                            }
                                        }),
                                        ...reseller.settings.vehicleTypes.sort((a, b) => {
                                            const aArchived = a.isArchived ? 1 : 0
                                            const bArchived = b.isArchived ? 1 : 0

                                            return aArchived - bArchived
                                        }).map((vehicleType) => ({
                                            value: vehicleType._id,
                                            title: `${vehicleType.name}`,
                                            icon: `mdi mdi-${vehicleType.icon}`,
                                            hidden: vehicleType.isArchived
                                        }))
                                    ]}
                                    value={route.vehicleTypeId}
                                    subvalue={route.vehicleId}
                                    readOnly={route.completed}
                                    onChange={(event: VehicleSelectEvent) => {
                                        const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === event.target.value)

                                        if (!vehicleType) {
                                            return
                                        }

                                        const vehicle = vehicles.find((v) => v._id === event.target.subvalue)

                                        const update = {
                                            vehicleTypeId: vehicleType._id,
                                            vehicleId: vehicle?._id || '',
                                            vehicleType: vehicleType.routeProfile || '',
                                            customSpeed: false,
                                            speedDifference: 0
                                        }

                                        if (vehicleType?.speedDifferenceEnabled) {
                                            update.customSpeed = true
                                            const speedDifference = vehicleType.speedDifferenceMode === 'faster' ? -vehicleType.speedDifference : vehicleType.speedDifference
                                            update.speedDifference = speedDifference
                                        } else {
                                            update.customSpeed = false
                                            update.speedDifference = 0
                                        }

                                        OrderActions.updateRoute(route._id, update)
                                    }}
                                    isInvalid={routeOverlaps}
                                    isInvalidIcon={routeOverlaps ? 'mdi mdi-alert' : undefined}
                                    groupSubvalues
                                />
                            </FormRow>

                            {route.loading &&
                                <Row style={{flexShrink: 0, justifyContent: 'center', alignItems: 'center', height: 42}}>
                                    <Spinner />
                                </Row>
                            }

                            {!route.loading && !route.completed &&
                                <Row style={{flexShrink: 0, justifyContent: 'space-between', gap: 12, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>

                                    <ToolTip text={routeHasNotStartedAddresses ? Languages.routeHasNotStartedAddresses['NL'] : Languages.nrOfStops['NL']} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0, color: routeHasNotStartedAddresses && Colors.warningBright}} icon='mdi mdi-home-outline' />

                                            <P>{stopBundles.countStops(route.addresses || [])}</P>
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text={(tooMuchColli || tooHeavy) ? `Laadvermogen overschreden (${tooHeavy ? 'Gewicht' : typeof tooMuchColli === 'string' ? getColliTypeName(tooMuchColli, reseller) : 'Colli'})` : 'Aantal colli'} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0, ...((tooMuchColli || tooHeavy) && {color: Colors.errorBright})}} icon='mdi mdi-package-variant-closed' />

                                            <P style={{...((tooMuchColli || tooHeavy) && {color: Colors.errorBright})}}>{amountOfColli}</P>
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text={tooFar ? 'Bereik overschreden' : 'Resterende afstand'} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0, ...(tooFar && {color: Colors.errorBright})}} icon='mdi mdi-road-variant' />

                                            {typeof route.distance === 'number' &&
                                                <P style={{...(tooFar && {color: Colors.errorBright})}}>
                                                    {route.distance < 10000 ?
                                                        `${(route.distance / 1000).toFixed(1)} km` :
                                                        `${(route.distance / 1000).toFixed(0)} km`
                                                    }
                                                </P>
                                            }
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text='Resterende duur' placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0}}
                                                icon={!route.startTime && !inTimeSlots ? 'mdi mdi-timer-sand-complete' : 'mdi mdi-timer-sand'}
                                            />

                                            {typeof route.duration === 'number' &&
                                                <P>{formatDuration(route.duration)}</P>
                                            }
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text='Verwachte eindtijd' placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0, color: (route.plannedStartTime || route.startTime) && !inTimeSlots ? (afterTimeSlot ? Colors.errorBright : Colors.warningBright) : undefined}}
                                                icon={(route.plannedStartTime || route.startTime) && !inTimeSlots ? 'mdi mdi-clock-alert-outline' : 'mdi mdi-clock-check-outline'}
                                            />

                                            {route.estimatedEndTime &&
                                                <P>{moment(route.estimatedEndTime).format('HH:mm')}</P>
                                            }
                                        </Stack>
                                    </ToolTip>
                                </Row>
                            }

                            {route.completed &&
                                <Row style={{flexShrink: 0, justifyContent: 'space-between', gap: 12, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>
                                    <ToolTip text={Languages.completed['NL']} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-check-bold' />

                                            <P>{route.success || 0}</P>
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text={Languages.notCompleted['NL']} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-close-thick' />

                                            <P>{route.notCompleted || 0}</P>
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text={Languages.tooLate['NL']} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-clock-remove-outline' />

                                            <P>{route.tooLate || 0}</P>
                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text={Languages.duration['NL']} placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0}}
                                                icon='mdi mdi-timer-sand-complete'
                                            />

                                            {typeof completedDuration === 'number' &&
                                                <P>{formatDuration(completedDuration)}</P>
                                            }

                                        </Stack>
                                    </ToolTip>

                                    <ToolTip text='Afwijking berekende duur' placement='top' style={{flex: 1}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 6}} icon='mdi mdi-flag-checkered' />

                                            {typeof completedDelay === 'number' &&
                                                <P>{formatDuration(completedDelay, true)}</P>
                                            }
                                        </Stack>
                                    </ToolTip>
                                </Row>
                            }
                        </>
                    }
                </Stack>

                <Row
                    style={{flexShrink: 0,
                        width: 24,
                        backgroundColor: Colors.backgroundSelected,
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => OrderActions.setSelectedRoute(selected ? '' : route._id)}
                >
                    <Icon style={{marginRight: 6}} icon={selected ? 'mdi mdi-chevron-left' : 'mdi mdi-chevron-right'} />
                </Row>
            </Row>

            <Popup ref={popupRef} />
        </Card>
    )

    if (!bulkAction) {
        return card
    }

    // Checkbox in front of the card, under the select all checkbox, so the cards line up with the bulk action button
    return (
        <Row
            style={{alignItems: 'flex-start'}}
            onMouseDown={(event) => {
                // Prevent text selection when using shift key to select a range of routes
                if (event.shiftKey) {
                    event.preventDefault()
                }
            }}
        >
            <ToolTip text={bulkDisabledReason} placement='right' style={{marginTop: 6}}>
                <Check
                    checked={bulkSelected && !bulkDisabledReason}
                    disabled={!!bulkDisabledReason || bulkRunning}
                    onChange={(event: {shiftKey?: boolean}) => onToggleBulkSelect?.(route._id, event)}
                />
            </ToolTip>

            {card}
        </Row>
    )
})
